import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useGamification } from "@/hooks/useGamification";
import { supabase } from "@/integrations/supabase/client";
import { logBoardCreationAttempt } from "@/lib/boardCreationLogger";
import { generateOptimalOklchColor } from "@/lib/colorUtils";
import { retryWithBackoff } from "@/lib/retryWithBackoff";

export interface CreateBoardPersona {
  role_name: string;
  mission: string;
  principles: string;
  competencies: string;
  key_relationships: string;
  typical_questions: string[];
}

interface CreateBoardOptions {
  boardName: string;
  description: string;
  personas: CreateBoardPersona[];
  boardType: "prebuilt" | "custom" | "ai_generated";
  templateBoardId?: string;
  onSuccess?: (boardId: string) => void;
}

export const useCreateBoard = () => {
  const { user } = useAuth();
  const { awardPoints } = useGamification();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  const createBoard = async ({
    boardName,
    description,
    personas,
    boardType,
    templateBoardId,
    onSuccess
  }: CreateBoardOptions) => {
    if (!user?.id) return;
    if (!boardName.trim()) {
      toast.error("Board name cannot be empty");
      return;
    }

    setIsCreating(true);

    try {
      // 1. Fetch Workspace
      const workspace = await retryWithBackoff(async () => {
        const { data, error } = await supabase
          .from("workspaces")
          .select("id")
          .eq("user_id", user.id)
          .order("created_at", { ascending: true })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error("No workspace found");
        return data;
      });

      await logBoardCreationAttempt({
        userId: user.id,
        boardType,
        step: "workspace_fetch",
        status: "success",
        templateBoardId
      });

      // 2. Calculate Color
      const { data: existingBoards } = await supabase
        .from("boards")
        .select("color")
        .eq("workspace_id", workspace.id);
      
      const optimalColor = generateOptimalOklchColor(existingBoards?.map(b => b.color) || []);

      // 3. Create Board
      const newBoard = await retryWithBackoff(async () => {
        const { data, error } = await supabase
          .from("boards")
          .insert({
            name: boardName.trim(),
            description,
            workspace_id: workspace.id,
            template_board_id: templateBoardId,
            color: optimalColor
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      });

      await logBoardCreationAttempt({
        userId: user.id,
        boardType,
        step: "board_create",
        status: "success",
        boardId: newBoard.id,
        templateBoardId
      });

      awardPoints('board');

      // 4. Create Personas
      const personasToInsert = personas.map(p => ({
        board_id: newBoard.id,
        role_name: p.role_name,
        mission: p.mission,
        principles: p.principles,
        competencies: p.competencies,
        key_relationships: p.key_relationships,
        typical_questions: JSON.stringify(p.typical_questions),
      }));

      await retryWithBackoff(async () => {
        const { error } = await supabase.from("personas").insert(personasToInsert);
        if (error) throw error;
      });

      await logBoardCreationAttempt({
        userId: user.id,
        boardType,
        step: "personas_create",
        status: "success",
        boardId: newBoard.id,
        templateBoardId
      });

      // 5. Create Project
      const newProject = await retryWithBackoff(async () => {
        const { data, error } = await supabase
          .from("projects")
          .insert({
            board_id: newBoard.id,
            name: "My First Project"
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      });

      await logBoardCreationAttempt({
        userId: user.id,
        boardType,
        step: "project_create",
        status: "success",
        boardId: newBoard.id,
        templateBoardId
      });

      // 6. Create Session
      const newSession = await retryWithBackoff(async () => {
        const { data, error } = await supabase
          .from("sessions")
          .insert({
            project_id: newProject.id,
            name: "Session 1"
          })
          .select()
          .single();

        if (error) throw error;
        return data;
      });

      await logBoardCreationAttempt({
        userId: user.id,
        boardType,
        step: "session_create",
        status: "success",
        boardId: newBoard.id,
        templateBoardId
      });

      toast.success("Board created successfully! Redirecting...");
      
      if (onSuccess) onSuccess(newBoard.id);
      
      // Small delay to ensure all database operations complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      navigate(`/sessions/${newSession.id}`);

    } catch (error) {
      console.error("Error creating board:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to create board";
      toast.error(errorMessage);
      
      await logBoardCreationAttempt({
        userId: user.id,
        boardType,
        step: "client_crash",
        status: "failure",
        errorMessage,
        templateBoardId
      });
    } finally {
      setIsCreating(false);
    }
  };

  return { createBoard, isCreating };
};
